// Powiadomienia jednego filtra — interwały WŁAŚCICIELA filtra (`UserFilterPreferences`),
// dokładnie to, co klient ustawia sam we froncie. Kanały (e-mail/SMS/aplikacja) są per
// osoba i typ powiadomienia, nie per filtr — karta je pokazuje, ale ich tu nie zmienia.
//
// Backend zawsze zapisuje ustawienia właściciela filtra, nigdy admina — panel tylko o tym mówi.

import { useEffect, useState } from "react";
import { Alert, Button, Card, Checkbox, Popconfirm, Space, Table, Tag, Typography } from "antd";
import type { DeliveryContext, FilterDictionaries, NotificationSubscription } from "../types/filterAdmin";
import { CHANNEL_LABELS, NOTIFICATION_TYPE_LABELS, SEND_INTERVAL_LABELS } from "../types/filterAdmin";

const { Text, Paragraph } = Typography;

export type NotificationDraft = {
  intervals: string[];
};

/** Typ powiadomienia, który niesie dany interwał (od niego zależą kanały). */
const INTERVAL_TYPE: Record<string, string> = {
  instantly: "instant_raport",
  daily: "daily_raport",
  weekly: "weekly_raport",
  monthly: "monthly_raport",
};

/** Powody, dla których wysyłka nie dojdzie mimo ustawionych interwałów. */
function deliveryWarnings(draft: NotificationDraft, subscription: NotificationSubscription | null, delivery: DeliveryContext | null): string[] {
  const warnings: string[] = [];
  const channels = subscription?.channels ?? [];
  for (const interval of draft.intervals) {
    const type = INTERVAL_TYPE[interval];
    const enabled = channels.filter((c) => c.type === type && c.enabled).map((c) => c.method);
    if (channels.length > 0 && !enabled.some((m) => m === "email" || m === "phone" || m === "app")) {
      warnings.push(
        `„${SEND_INTERVAL_LABELS[interval] ?? interval}”: klient ma wyłączone wszystkie kanały typu „${NOTIFICATION_TYPE_LABELS[type] ?? type}” — nic nie dostanie.`,
      );
    }
    if (enabled.includes("phone") && delivery && !delivery.phone) {
      warnings.push(
        `„${SEND_INTERVAL_LABELS[interval] ?? interval}”: SMS jest włączony, ale profil klienta nie ma numeru telefonu (ContactData.telephone_number).`,
      );
    }
  }
  return warnings;
}

export function NotificationPreferenceCard({
  subscription,
  delivery,
  dictionaries,
  saving,
  onSave,
  onDelete,
}: {
  subscription: NotificationSubscription | null;
  delivery: DeliveryContext | null;
  dictionaries: FilterDictionaries | null;
  saving: boolean;
  onSave: (draft: NotificationDraft) => void;
  onDelete: () => void;
}) {
  const saved = subscription?.intervals ?? [];
  const [draft, setDraft] = useState<NotificationDraft>({ intervals: saved });

  useEffect(() => {
    setDraft({ intervals: subscription?.intervals ?? [] });
  }, [subscription]);

  const warnings = deliveryWarnings(draft, subscription, delivery);
  const unchanged = draft.intervals.length === saved.length && draft.intervals.every((i) => saved.includes(i));
  const relevantTypes = new Set(draft.intervals.map((i) => INTERVAL_TYPE[i]).filter(Boolean));
  const channelRows = (subscription?.channels ?? []).filter((c) => relevantTypes.has(c.type));

  return (
    <Card
      title="Powiadomienia o tym filtrze"
      extra={
        saved.length > 0 ? (
          <Text type="secondary">włączone: {saved.map((i) => SEND_INTERVAL_LABELS[i]?.split(" (")[0] ?? i).join(", ")}</Text>
        ) : (
          <Text type="secondary">wyłączone — filtr jest tylko zapisany</Text>
        )
      }
    >
      <Paragraph type="secondary">
        Kiedy właściciel filtra dostaje powiadomienia z tego filtra. To samo ustawia klient we
        froncie. Kanały (e-mail, SMS, aplikacja) są ustawieniem osoby per typ powiadomienia.
      </Paragraph>

      <Text strong>Częstotliwość</Text>
      <div style={{ marginTop: 4 }}>
        <Checkbox.Group
          value={draft.intervals}
          options={(dictionaries?.send_intervals ?? ["instantly", "daily", "weekly", "monthly"]).map((interval) => ({
            value: interval,
            label: SEND_INTERVAL_LABELS[interval] ?? interval,
          }))}
          onChange={(intervals) => setDraft({ intervals: intervals as string[] })}
        />
      </div>

      {channelRows.length > 0 && (
        <Table
          style={{ marginTop: 16 }}
          size="small"
          pagination={false}
          rowKey={(row) => `${row.type}:${row.method}`}
          dataSource={channelRows}
          columns={[
            { title: "Typ", dataIndex: "type", render: (type: string) => NOTIFICATION_TYPE_LABELS[type] ?? type },
            { title: "Kanał", dataIndex: "method", render: (method: string) => CHANNEL_LABELS[method] ?? method },
            {
              title: "Stan",
              dataIndex: "enabled",
              render: (enabled: boolean) => (enabled ? <Tag color="green">włączony</Tag> : <Tag>wyłączony</Tag>),
            },
          ]}
        />
      )}

      {warnings.map((warning, index) => (
        <Alert key={index} type="warning" showIcon style={{ marginTop: 16 }} message="Wysyłka może nie dojść" description={warning} />
      ))}

      <Space style={{ marginTop: 16 }}>
        <Button type="primary" loading={saving} disabled={unchanged} onClick={() => onSave(draft)}>
          Zapisz ustawienia powiadomień
        </Button>
        {saved.length > 0 && (
          <Popconfirm
            title="Wyłączyć powiadomienia filtra?"
            description="Filtr zostanie, ale przestanie cokolwiek wysyłać właścicielowi. To samo robi klient, odznaczając częstotliwości we froncie."
            okText="Wyłącz"
            cancelText="Anuluj"
            onConfirm={onDelete}
          >
            <Button danger loading={saving}>
              Wyłącz powiadomienia
            </Button>
          </Popconfirm>
        )}
      </Space>
    </Card>
  );
}
